import { SessaoUsuario } from "../api/auth";
import { getJson, putJson } from "../api/client";
import { buscarAtivos, buscarLeituras, Ativo } from "../api/m5";

interface LimiteEstacao {
  sensor_id: string;
  tag: string;
  indice: number;
  nome: string | null;
  alert_rule_id: string | null;
  limite_inferior: number | null;
  limite_superior: number | null;
  ativo: boolean | null;
}

export function montarConfiguracoes(container: HTMLElement, sessao: SessaoUsuario): void {
  const podeConfigurarAlarmes = sessao.permissions.includes("alarm:manage");

  container.innerHTML = `
    <div class="card" style="max-width: 420px;">
      <h2 style="margin-top:0;">Trocar senha</h2>
      <div class="filtros" style="flex-direction: column; align-items: stretch;">
        <label>Senha atual <input id="cfg-senha-atual" type="password" autocomplete="current-password" /></label>
        <label>Nova senha <input id="cfg-senha-nova" type="password" autocomplete="new-password" /></label>
        <div class="erro" id="cfg-erro" style="color: var(--color-danger, #b00020); font-size: 0.85rem; min-height: 1.2em;"></div>
        <button class="botao" id="cfg-salvar">Salvar nova senha</button>
      </div>
    </div>
    ${podeConfigurarAlarmes ? `
    <div class="card" style="max-width: 640px;">
      <h2 style="margin-top:0;">Limites de alerta por estação</h2>
      <p class="aviso" style="margin-top:-8px;">Define o limite inferior e/ou superior de uma variável para uma estação específica. Sem limite definido, nenhum alarme é avaliado para essa combinação.</p>
      <div class="filtros">
        <label>Estação <select id="cfg-lim-estacao"></select></label>
        <label>Variável (tag) <select id="cfg-lim-tag"></select></label>
        <label>Limite inferior <input id="cfg-lim-inferior" type="number" step="any" placeholder="opcional"></label>
        <label>Limite superior <input id="cfg-lim-superior" type="number" step="any" placeholder="opcional"></label>
        <button class="botao secundario" id="cfg-lim-salvar">Salvar limite</button>
      </div>
      <div class="erro" id="cfg-lim-erro" style="color: var(--color-danger, #b00020); font-size: 0.85rem; min-height: 1.2em;"></div>
      <table>
        <thead><tr><th>Variável</th><th>Limite inferior</th><th>Limite superior</th><th></th></tr></thead>
        <tbody id="cfg-lim-corpo"></tbody>
      </table>
    </div>
    ` : ""}
  `;

  const senhaAtualEl = container.querySelector<HTMLInputElement>("#cfg-senha-atual")!;
  const senhaNovaEl = container.querySelector<HTMLInputElement>("#cfg-senha-nova")!;
  const erroEl = container.querySelector<HTMLElement>("#cfg-erro")!;
  const botaoEl = container.querySelector<HTMLButtonElement>("#cfg-salvar")!;

  botaoEl.addEventListener("click", async () => {
    erroEl.textContent = "";
    const senhaAtual = senhaAtualEl.value;
    const senhaNova = senhaNovaEl.value;
    if (!senhaAtual || !senhaNova) {
      erroEl.textContent = "preencha a senha atual e a nova senha";
      return;
    }

    const sessaoAtualFetch = await fetch("/m4/auth/me/senha", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessao.token}`,
      },
      body: JSON.stringify({ senha_atual: senhaAtual, senha_nova: senhaNova }),
    });

    if (sessaoAtualFetch.ok) {
      senhaAtualEl.value = "";
      senhaNovaEl.value = "";
      erroEl.style.color = "var(--color-success, #2e7d32)";
      erroEl.textContent = "senha atualizada com sucesso.";
    } else {
      erroEl.style.color = "var(--color-danger, #b00020)";
      const corpo = await sessaoAtualFetch.json().catch(() => ({}));
      erroEl.textContent = corpo.erro || "falha ao trocar senha";
    }
  });

  if (podeConfigurarAlarmes) {
    montarLimitesAlerta(container);
  }
}

async function montarLimitesAlerta(container: HTMLElement): Promise<void> {
  const estacaoEl = container.querySelector<HTMLSelectElement>("#cfg-lim-estacao")!;
  const tagEl = container.querySelector<HTMLSelectElement>("#cfg-lim-tag")!;
  const inferiorEl = container.querySelector<HTMLInputElement>("#cfg-lim-inferior")!;
  const superiorEl = container.querySelector<HTMLInputElement>("#cfg-lim-superior")!;
  const erroEl = container.querySelector<HTMLElement>("#cfg-lim-erro")!;
  const corpoEl = container.querySelector<HTMLElement>("#cfg-lim-corpo")!;
  const salvarEl = container.querySelector<HTMLButtonElement>("#cfg-lim-salvar")!;

  let ativos: Ativo[] = [];
  try {
    ativos = await buscarAtivos();
  } catch {
    erroEl.textContent = "falha ao carregar estações.";
    return;
  }
  estacaoEl.innerHTML = ativos.map((a) => `<option value="${a.id}">${a.nome}</option>`).join("");

  const amostra = await buscarLeituras({ limit: 500 }).catch(() => []);
  const tags = [...new Set(amostra.map((l) => l.tag))].sort();
  tagEl.innerHTML = tags.map((t) => `<option value="${t}">${t}</option>`).join("");

  async function carregarLimites() {
    corpoEl.innerHTML = "";
    if (!estacaoEl.value) return;
    try {
      const limites = await getJson<LimiteEstacao[]>(`/m6/api/alert-rules/por-estacao?asset_id=${estacaoEl.value}`);
      const comLimite = limites.filter((l) => l.limite_inferior != null || l.limite_superior != null);
      corpoEl.innerHTML = comLimite.length
        ? comLimite
            .map(
              (l) => `<tr>
                <td>${l.tag}</td><td>${l.limite_inferior ?? "—"}</td><td>${l.limite_superior ?? "—"}</td>
                <td><button class="botao secundario" data-editar-tag="${l.tag}" data-editar-inferior="${l.limite_inferior ?? ""}" data-editar-superior="${l.limite_superior ?? ""}">Editar</button></td>
              </tr>`
            )
            .join("")
        : '<tr><td colspan="4" class="aviso">Nenhum limite configurado para esta estação ainda.</td></tr>';

      corpoEl.querySelectorAll<HTMLButtonElement>("[data-editar-tag]").forEach((botao) => {
        botao.addEventListener("click", () => {
          tagEl.value = botao.dataset.editarTag!;
          inferiorEl.value = botao.dataset.editarInferior || "";
          superiorEl.value = botao.dataset.editarSuperior || "";
          erroEl.textContent = "";
        });
      });
    } catch {
      corpoEl.innerHTML = '<tr><td colspan="4" class="aviso">falha ao carregar limites.</td></tr>';
    }
  }

  estacaoEl.addEventListener("change", carregarLimites);
  await carregarLimites();

  salvarEl.addEventListener("click", async () => {
    erroEl.textContent = "";
    const inferior = inferiorEl.value.trim();
    const superior = superiorEl.value.trim();
    if (!inferior && !superior) {
      erroEl.textContent = "informe ao menos o limite inferior ou superior";
      return;
    }
    try {
      await putJson("/m6/api/alert-rules/por-estacao", {
        asset_id: estacaoEl.value,
        tag: tagEl.value,
        limite_inferior: inferior ? Number(inferior) : null,
        limite_superior: superior ? Number(superior) : null,
      });
      inferiorEl.value = "";
      superiorEl.value = "";
      await carregarLimites();
    } catch (e) {
      erroEl.textContent = "falha ao salvar limite: " + e;
    }
  });
}
